import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Class, CreateClassRequest, UpdateClassRequest } from '../../context/types';
import { createClass, updateClass } from '../../services/api';

interface ClassFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  classToEdit?: Class | null;
  onClose: () => void;
  onSave: () => void;
}

// Constants for dropdown options
const TAGS = ['adult', 'kid', 'young adult'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Generate time slots from 9:00am to 8:00pm in 15-minute increments
const generateTimeSlots = (): string[] => {
  const slots: string[] = [];
  for (let hour = 9; hour <= 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      if (hour === 20 && minute > 0) break; // Stop at 8:00pm

      const period = hour < 12 ? 'am' : 'pm';
      const displayHour = hour > 12 ? hour - 12 : hour;
      const displayMinute = minute.toString().padStart(2, '0');

      slots.push(`${displayHour}:${displayMinute}${period}`);
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const ClassFormModal: React.FC<ClassFormModalProps> = ({
  show,
  mode,
  classToEdit,
  onClose,
  onSave,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tag: '',
    day: '',
    time: '',
  });

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Initialize form data when modal opens or classToEdit changes
  useEffect(() => {
    if (show) {
      if (mode === 'edit' && classToEdit) {
        setFormData({
          name: classToEdit.name,
          description: classToEdit.description || '',
          tag: classToEdit.tag,
          day: classToEdit.day,
          time: classToEdit.time,
        });
      } else {
        // Reset form for create mode
        setFormData({
          name: '',
          description: '',
          tag: '',
          day: '',
          time: '',
        });
      }
      setErrors({});
      setApiError(null);
    }
  }, [show, mode, classToEdit]);

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Clear API error when user makes changes
    if (apiError) {
      setApiError(null);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Class name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Class name must be 100 characters or less';
    }

    if (formData.description.length > 100) {
      newErrors.description = 'Description must be 100 characters or less';
    }

    if (!formData.day) {
      newErrors.day = 'Day is required';
    }

    if (!formData.time) {
      newErrors.time = 'Time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const requestData: CreateClassRequest | UpdateClassRequest = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        tag: formData.tag,
        day: formData.day,
        time: formData.time,
      };

      if (mode === 'create') {
        await createClass(requestData as CreateClassRequest);
      } else if (mode === 'edit' && classToEdit) {
        await updateClass(classToEdit.id, requestData as UpdateClassRequest);
      }

      // Success - call onSave callback to refresh the list
      onSave();
      onClose();
    } catch (err: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} class:`, err);

      let errorMessage: string;
      if (err.response) {
        // Server responded with error
        if (err.response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.detail || 'Invalid data provided.';
        } else {
          errorMessage = err.response.data?.detail ||
                        `Failed to ${mode === 'create' ? 'create' : 'update'} class.`;
        }
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = `Failed to ${mode === 'create' ? 'create' : 'update'} class.`;
      }

      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" keyboard={!isSubmitting} data-bs-theme="dark">
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          {mode === 'create' ? 'Add New Class' : 'Edit Class'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {apiError && (
            <Alert variant="danger" dismissible onClose={() => setApiError(null)}>
              {apiError}
            </Alert>
          )}

          {/* Name Field */}
          <Form.Group className="mb-3">
            <Form.Label>
              Class Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter class name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              isInvalid={!!errors.name}
              maxLength={100}
              disabled={isSubmitting}
              autoFocus
              className="bg-dark text-light"
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Description Field */}
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Enter class description (optional)"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              isInvalid={!!errors.description}
              maxLength={100}
              disabled={isSubmitting}
              className="bg-dark text-light"
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              {formData.description.length}/100 characters
            </Form.Text>
          </Form.Group>

          {/* Tag Field */}
          <Form.Group className="mb-3">
            <Form.Label>
              Tag
            </Form.Label>
            <Form.Select
              value={formData.tag}
              onChange={(e) => handleInputChange('tag', e.target.value)}
              isInvalid={!!errors.tag}
              disabled={isSubmitting}
            >
              <option value="">Select tag...</option>
              {TAGS.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.tag}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Day Field */}
          <Form.Group className="mb-3">
            <Form.Label>
              Day <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={formData.day}
              onChange={(e) => handleInputChange('day', e.target.value)}
              isInvalid={!!errors.day}
              disabled={isSubmitting}
            >
              <option value="">Select day...</option>
              {DAYS.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.day}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Time Field */}
          <Form.Group className="mb-3">
            <Form.Label>
              Time <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
              isInvalid={!!errors.time}
              disabled={isSubmitting}
            >
              <option value="">Select time...</option>
              {TIME_SLOTS.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.time}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {mode === 'create' ? 'Creating...' : 'Saving...'}
              </>
            ) : (
              mode === 'create' ? 'Create Class' : 'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ClassFormModal;
